import React, { useCallback, useEffect, useRef, useState } from "react";
import { useRouteMatch } from "react-router-dom";
import { Box, Hidden, Typography } from "@material-ui/core";

import Authorities from "../../../../auth/authorities";
import withAuthority from "../../../../components/Auth/withAuthority";
import DefaultAlert from "../../../../components/alerts/DefaultAlert";
import PageHeaderMobile from "../../../../components/common/PageHeader/PageHeaderMobile";
import {
  fetchHistoricalDelays,
  fetchHistoricalStations,
  fetchHistoricalSummary,
} from "../../../../services/insightApp/historicalAnalytics";

import DelayedOrdersTable, { DelayedOrder } from "./DelayedOrdersTable";
import HistoricalFilters from "./HistoricalFilters";
import HistoricalSummaryCards, {
  HistoricalSummary,
} from "./HistoricalSummaryCards";
import OrderTimelineModal from "./OrderTimelineModal";
import StationComparisonTable, { StationAverage } from "./StationComparisonTable";
import {
  defaultHistoricalFilters,
  HistoricalFilterValues,
  toQueryParams,
} from "./historicalUtils";

/**
 * INSIGHT-HIST-13 — Historical Station performance.
 *
 * Three parts, each answering the next question: how are stations doing
 * overall, which station is slowest, and which of its orders were late. A late
 * order opens its timeline (INSIGHT-HIST-16).
 *
 * Choosing a station in the table narrows the delayed orders to it, and the
 * station filter above does the same for every panel.
 */

const HistoricalStationPage: React.FunctionComponent<{}> = () => {
  const match: any = useRouteMatch();
  const locationId = match?.params?.locationId;

  const [filters, setFilters] = useState<HistoricalFilterValues>(
    defaultHistoricalFilters(),
  );
  const [summary, setSummary] = useState<HistoricalSummary | null>(null);
  const [stations, setStations] = useState<StationAverage[]>([]);
  const [delays, setDelays] = useState<DelayedOrder[]>([]);
  const [isSummaryLoading, setIsSummaryLoading] = useState(true);
  const [isStationsLoading, setIsStationsLoading] = useState(true);
  const [isDelaysLoading, setIsDelaysLoading] = useState(true);
  const [summaryError, setSummaryError] = useState("");
  const [error, setError] = useState("");
  const [selectedOrder, setSelectedOrder] = useState<DelayedOrder | null>(null);

  // A station chosen from the table narrows the delayed orders below it.
  const stationId = filters.stationId || "";

  // Each load gets an id; a response from an older load is ignored, so a slow
  // earlier request can't overwrite a newer one.
  const requestIdRef = useRef(0);
  const load = useCallback(async () => {
    if (!locationId) {
      return;
    }

    const requestId = ++requestIdRef.current;
    const isStale = () => requestId !== requestIdRef.current;

    const params = { ...toQueryParams(filters), metricType: "STATION" };

    setIsSummaryLoading(true);
    setIsStationsLoading(true);
    setIsDelaysLoading(true);
    setSummaryError("");

    try {
      const res = await fetchHistoricalSummary(locationId, params);
      if (isStale()) return;
      setSummary(res?.data?.data || null);
    } catch (err) {
      if (isStale()) return;
      setSummary(null);
      // An error is kept separate from an empty result on purpose.
      setSummaryError("The station summary could not be loaded.");
    } finally {
      if (!isStale()) {
        setIsSummaryLoading(false);
      }
    }

    try {
      // The comparison always shows every station, so the station filter is
      // left out of this one call.
      const { stationId: ignored, ...comparisonParams } = params as any;
      const res = await fetchHistoricalStations(locationId, comparisonParams);
      if (isStale()) return;
      setStations(res?.data?.data?.stations || []);
    } catch (err) {
      if (isStale()) return;
      setStations([]);
      setError("The station comparison could not be loaded.");
    } finally {
      if (!isStale()) {
        setIsStationsLoading(false);
      }
    }

    try {
      const res = await fetchHistoricalDelays(locationId, {
        ...params,
        limit: 50,
      });
      if (isStale()) return;
      setDelays(res?.data?.data?.orders || []);
    } catch (err) {
      if (isStale()) return;
      setDelays([]);
      setError("The delayed orders could not be loaded.");
    } finally {
      if (!isStale()) {
        setIsDelaysLoading(false);
      }
    }
  }, [locationId, filters]);

  useEffect(() => {
    document.title = "Insight - Station Time Report";
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleSelectStation = (selected: string) => {
    // Clicking the station already selected clears it again.
    setFilters((previous) => ({
      ...previous,
      stationId: previous.stationId === selected ? "" : selected,
    }));
  };

  return (
    <>
      <Hidden lgUp>
        <PageHeaderMobile />
      </Hidden>

      <Box style={{ marginTop: "18px" }}>
        <Typography variant="h5" gutterBottom>
          Station Time Report
        </Typography>

        <HistoricalFilters
          values={filters}
          onChange={setFilters}
          showStation
          disabled={isSummaryLoading && isStationsLoading && isDelaysLoading}
        />

        <Box mt={2}>
          <HistoricalSummaryCards
            summary={summary}
            isLoading={isSummaryLoading}
            error={summaryError}
          />
        </Box>

        <Box mt={2}>
          <StationComparisonTable
            stations={stations}
            isLoading={isStationsLoading}
            selectedStationId={stationId}
            onSelectStation={handleSelectStation}
          />
        </Box>

        <Box mt={2}>
          <DelayedOrdersTable
            orders={delays}
            isLoading={isDelaysLoading}
            onSelectOrder={setSelectedOrder}
          />
        </Box>
      </Box>

      {selectedOrder && (
        <OrderTimelineModal
          open={!!selectedOrder}
          onClose={() => setSelectedOrder(null)}
          locationId={locationId}
          orderNo={selectedOrder.orderNo}
          businessDate={selectedOrder.businessDate}
        />
      )}

      <DefaultAlert
        open={!!error}
        handleClose={() => setError("")}
        message={error}
        severity="error"
      />
    </>
  );
};

export default withAuthority(
  HistoricalStationPage,
  Authorities.CUSTOMER_LIST_READ
);
